import { useState, useCallback, useEffect } from 'react';
import type { Dispatch } from 'redux';
import { Link } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';

import { TemplatesItem } from 'PersonalAccountPackage/MainModule/components/TemplatesItem';
import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { TitleWithAddButton } from 'UIPackage/MainModule/components/TitleWithAddButton';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { CHECKLIST_TEMPLATE_EXPORT_SUCCESSFULLY } from 'CorePackage/MainModule/constants/notices';
import notificationStyles from 'CorePackage/NotificationModule/components/Notification.module.scss';
import { ROUTE_TEMPLATES_EXPORT } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  getAccountOrganizationsData,
  getAccountCheckListsTemplates,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { IChecklistTemplate } from 'APIPackage/AccountModule/interfaces/IChecklistTemplate';
import { IPageChecklistTemplate } from 'APIPackage/AccountModule/interfaces/IPageChecklistTemplate';
import { IAccountTemplate } from 'CorePackage/AccountModule/interfaces/IAccountTemplate';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';

import styles from './CheckLists.module.scss';
import { CheckListForm } from './CheckListForm';
import { ICheckListForm } from '../interfaces/ICheckListForm';
import {
  checkListsChangeDefault,
  checkListsDelete,
  checkListsExportById,
  checkListsFormQuestionDelete,
  checkListsGetTemplateList,
  checkListsGetTemplateListNextPage,
  checkListsInitPage,
  checkListsResetPage,
  checkListsSetActiveTemplateId,
  checkListsUpdate,
} from '../actions/checkListsAction';
import {
  getCheckListsTemplateList,
  getCheckListsTotalCount,
  getCheckListsActiveTemplateId,
  getCheckListsPage,
} from '../selectors/checkListsSelectors';
import { USER_CHECKLIST_PAGE_SIZE } from '../constants/userChecklistPageSize';

const emptyChecklistTemplate = {
  id: undefined,
  name: '',
  approvalDetails: '',
  byDefault: false,
  knoOrganizationId: undefined,
  questions: undefined,
};

interface ICheckListsStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  isErknm: boolean;
  accountCheckListsTemplates: {
    [key: string]: IAccountTemplate<IPageChecklistTemplate> | undefined;
  };
  templatesList: IChecklistTemplate[];
  totalCount: number;
  activeTemplateId: number | 'new' | undefined;
  templatesPage: number;
  knoOrganisations: IUserOrganisation[];
}

const CheckListsWithoutStore = ({
  accountCheckListsTemplates,
  activeTemplateId,
  isErknm,
  isProsecutorOrSuperProsecutor,
  templatesList,
  templatesPage,
  totalCount,
  knoOrganisations,
}: ICheckListsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(checkListsInitPage());

    return () => {
      dispatch(checkListsResetPage());
    };
  }, [dispatch]);

  useEffect(() => {
    if (accountCheckListsTemplates) {
      dispatch(checkListsGetTemplateList());
    }
  }, [dispatch, accountCheckListsTemplates]);

  const handleLoadButtonClick = useCallback(() => dispatch(checkListsGetTemplateListNextPage()), [dispatch]);

  const activeTemplate = templatesList.find(template => template.id === activeTemplateId) || emptyChecklistTemplate;
  const defaultKnoOrganisationId = knoOrganisations.find(item => item.byDefault)?.id;
  const [activatingTemplateId, setActivatingTemplateId] = useState<number | 'new' | undefined>();
  const [isFormReset, setIsFormReset] = useState(false);
  const isTemplateWarningModalVisible = typeof activatingTemplateId === 'number' || activatingTemplateId === 'new';
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | undefined>();
  const [isTemplateDeleteModalVisible, setIsTemplateDeleteModalVisible] = useState(false);
  const isLoadButtonVisible = totalCount && +USER_CHECKLIST_PAGE_SIZE * (templatesPage + 1) < totalCount;

  const exportChecklist = useCallback(
    (format: ExportFormatsEnum) =>
      activeTemplateId &&
      activeTemplateId !== 'new' &&
      dispatch(checkListsExportById(activeTemplateId, format, successRender)),
    [activeTemplateId, dispatch],
  );

  const successRender = (id: number) => (
    <>
      {CHECKLIST_TEMPLATE_EXPORT_SUCCESSFULLY}&nbsp;
      <Link className={notificationStyles.NotificationLink} to={ROUTE_TEMPLATES_EXPORT}>
        {id}
      </Link>
    </>
  );
  const exportToODS = useCallback(() => {
    exportChecklist(ExportFormatsEnum.ODS);
  }, [exportChecklist]);

  const exportToXLSX = useCallback(() => {
    exportChecklist(ExportFormatsEnum.XLSX);
  }, [exportChecklist]);

  const dropdownItems: IDropdownItem[] = [
    {
      id: 'export-ods',
      content: <DropdownItem onClick={exportToODS}>Экспорт проверочного листа в формате ODS</DropdownItem>,
    },
    {
      id: 'export-xlsx',
      content: <DropdownItem onClick={exportToXLSX}>Экспорт проверочного листа в формате XLSX</DropdownItem>,
    },
  ];

  const handleAddButtonClick = useCallback(() => dispatch(checkListsSetActiveTemplateId('new')), [dispatch]);

  const makeTemplatesItemCheckboxChangeHandler = (id?: number) => () => dispatch(checkListsChangeDefault(id));

  const handleTemplatesItemClick = (id: number) => () => {
    if (activeTemplateId !== id) {
      dispatch(checkListsSetActiveTemplateId(id));
    }
  };

  const handleTemplatesItemDelete = (id: number) => () => {
    setIsTemplateDeleteModalVisible(true);
    setDeletingTemplateId(id);
  };

  const handleFormReset = useCallback(() => setIsFormReset(false), []);

  const handleFormQuestionDelete = useCallback(
    (name: string, onUndo: () => void) => {
      dispatch(checkListsFormQuestionDelete(name, onUndo));
    },
    [dispatch],
  );

  const handleFormSubmit = useCallback((values: ICheckListForm) => dispatch(checkListsUpdate(values)), [dispatch]);

  const handleTemplateWarningModalApply = useCallback(() => {
    setActivatingTemplateId(undefined);
    setIsFormReset(true);
  }, []);

  const handleTemplateWarningModalClose = useCallback(() => setActivatingTemplateId(undefined), []);

  const handleTemplateDeleteModalApply = useCallback(() => {
    if (deletingTemplateId) {
      dispatch(checkListsDelete(deletingTemplateId));
    }
  }, [deletingTemplateId, dispatch]);

  const handleTemplateDeleteModalClose = useCallback(() => setIsTemplateDeleteModalVisible(false), []);

  const deletingTemplateName = templatesList.find(template => template.id === deletingTemplateId)?.name || '';

  const activeTemplateOrganisationId = activeTemplate.knoOrganizationId || defaultKnoOrganisationId;

  return (
    <div className={styles.Wrapper}>
      <div className={styles.Templates}>
        <div className={styles.Title}>
          <TitleWithAddButton
            disabled={isProsecutorOrSuperProsecutor}
            text="Шаблоны проверочных листов"
            onClick={handleAddButtonClick}
            id="checkListsAddButton"
          />
        </div>
        <ul className={styles.TemplatesList}>
          {!activeTemplate.id && (
            <li className={styles.TemplatesItem}>
              <TemplatesItem
                id="new"
                name="Наименование проверочного листа"
                groupName="checklistTemplates"
                isChecked={false}
                isActive
                onCheckboxChange={makeTemplatesItemCheckboxChangeHandler()}
              />
            </li>
          )}
          {templatesList.map(template => {
            return (
              <li className={styles.TemplatesItem} key={template.id}>
                <TemplatesItem
                  id={template.id}
                  name={template.name}
                  groupName="checklistTemplates"
                  isChecked={template.byDefault}
                  isActive={template.id === activeTemplateId}
                  onCheckboxChange={makeTemplatesItemCheckboxChangeHandler(template.id)}
                  onClick={handleTemplatesItemClick(template.id)}
                  onDelete={!isProsecutorOrSuperProsecutor ? handleTemplatesItemDelete(template.id) : undefined}
                />
              </li>
            );
          })}
          {!!isLoadButtonVisible && <Button text="Показать еще" color="load" onClick={handleLoadButtonClick} />}
        </ul>
        {!isErknm && (
          <div className={styles.DropdownButtonBlock}>
            <Dropdown items={dropdownItems} disabled={!activeTemplateId && activeTemplateId !== 0} />
          </div>
        )}
      </div>
      <div className={styles.TemplateInfo}>
        <div className="shared-group-label_margin">Общая информация</div>
        <CheckListForm
          templateName={activeTemplate.name}
          approvalDetails={activeTemplate.approvalDetails || ''}
          organisations={knoOrganisations}
          activeOrganisationId={activeTemplateOrganisationId}
          questions={activeTemplate.questions || []}
          isReset={isFormReset}
          onReset={handleFormReset}
          onQuestionDelete={handleFormQuestionDelete}
          onSubmit={handleFormSubmit}
        />
      </div>
      <ConfirmModal
        visible={isTemplateWarningModalVisible}
        title="Предупреждение"
        text="Шаблон проверочного листа был изменен, несохраненные изменения будут потеряны. Вы уверены?"
        cancelButtonText="Нет"
        onApply={handleTemplateWarningModalApply}
        onClose={handleTemplateWarningModalClose}
      />
      {templatesList.length > 0 && (
        <ConfirmModal
          visible={isTemplateDeleteModalVisible}
          title="Удаление шаблона проверочного листа"
          text={`Вы собираетесь удалить шаблон проверочного листа "${deletingTemplateName}". Удалить?`}
          applyButtonText="Удалить"
          cancelButtonText="Закрыть"
          onApply={handleTemplateDeleteModalApply}
          onClose={handleTemplateDeleteModalClose}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): ICheckListsStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  accountCheckListsTemplates: getAccountCheckListsTemplates(state),
  templatesList: getCheckListsTemplateList(state),
  totalCount: getCheckListsTotalCount(state),
  activeTemplateId: getCheckListsActiveTemplateId(state),
  templatesPage: getCheckListsPage(state),
  knoOrganisations: getAccountOrganizationsData(state).kno,
});

export const CheckLists = connect(mapStateToProps)(CheckListsWithoutStore);
